import { onBeforeUnmount, onMounted } from "vue";
import { MOBILE_BREAKPOINT } from "./useMobileLayout";
import { H5_MODE_EVENT, isH5Mode } from "@/utils/h5-mode";

/** Account for an on-screen keyboard without interpreting pinch zoom as a keyboard. */
export function useMobileViewportInsets() {
  let deferred: ReturnType<typeof setTimeout> | undefined;
  const update = () => {
    const viewport = window.visualViewport;
    const element = document.activeElement as HTMLElement | null;
    const editing = !!element && (element.matches("input, textarea") || element.isContentEditable);
    const gap = viewport && viewport.scale === 1 ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop) : 0;
    const keyboard = (isH5Mode() || window.innerWidth <= MOBILE_BREAKPOINT) && editing && gap > 120;
    const root = document.documentElement;
    root.classList.toggle("h5-keyboard-open", keyboard);
    root.style.setProperty("--h5-keyboard-inset", keyboard ? `${gap}px` : "0px");
    root.style.setProperty("--h5-navigation-height", keyboard ? "0px" : "62px");
  };
  const focusChanged = () => { clearTimeout(deferred); deferred = setTimeout(update, 0); };
  onMounted(() => {
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    window.addEventListener(H5_MODE_EVENT, update);
    document.addEventListener("focusin", focusChanged);
    document.addEventListener("focusout", focusChanged);
    update();
  });
  onBeforeUnmount(() => {
    clearTimeout(deferred);
    window.visualViewport?.removeEventListener("resize", update);
    window.visualViewport?.removeEventListener("scroll", update);
    window.removeEventListener("resize", update);
    window.removeEventListener(H5_MODE_EVENT, update);
    document.removeEventListener("focusin", focusChanged);
    document.removeEventListener("focusout", focusChanged);
    const root = document.documentElement;
    root.classList.remove("h5-keyboard-open");
    root.style.removeProperty("--h5-keyboard-inset");
    root.style.removeProperty("--h5-navigation-height");
  });
}
